import React, { useState, useEffect, useRef } from 'react';
import { Activity, FileCode, Trash2 } from 'lucide-react';
import { Button } from './Button';

interface ActivityEntry {
  id: string;
  time: string;
  agent: string;
  action: string;
  file: string;
}

interface AiActivityPanelProps {
  sessionId: string;
  className?: string;
}

export const AiActivityPanel: React.FC<AiActivityPanelProps> = ({ sessionId, className }) => {
  const [entries, setEntries] = useState<ActivityEntry[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const { agent, file, action } = (e as CustomEvent).detail ?? {};
      if (agent && file) {
        const time = new Date().toLocaleTimeString('en-US', {
          hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit',
        });
        setEntries((prev) => {
          const entry: ActivityEntry = {
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            time, agent, action: action || 'updated', file,
          };
          const next = [...prev, entry];
          return next.length > 500 ? next.slice(-400) : next;
        });
      }
    };
    window.addEventListener('tasking.aiActivity', handler);
    return () => window.removeEventListener('tasking.aiActivity', handler);
  }, []);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [entries]);

  const actionColor = (action: string) => {
    if (action === 'created') return 'hsl(142 70% 45%)';
    if (action === 'updated') return 'hsl(200 80% 55%)';
    if (action === 'deleted') return 'hsl(0 70% 55%)';
    return 'hsl(220 15% 60%)';
  };

  return (
    <div className={className} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 12px', borderBottom: '1px solid var(--border)',
        fontSize: 12, fontWeight: 600, color: 'var(--foreground)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Activity size={14} />
          AI Activity
          {entries.length > 0 && (
            <span style={{
              fontSize: 10, padding: '1px 6px', borderRadius: 10,
              background: 'hsl(142 70% 45% / 0.15)', color: 'hsl(142 70% 45%)',
            }}>
              {entries.length}
            </span>
          )}
        </div>
        {entries.length > 0 && (
          <Button variant="ghost" size="icon-sm" onClick={() => setEntries([])} title="Clear">
            <Trash2 size={12} />
          </Button>
        )}
      </div>

      <div ref={scrollRef} style={{
        flex: 1, overflowY: 'auto', padding: '4px 0',
        fontSize: 11, fontFamily: 'var(--font-mono, monospace)',
      }}>
        {entries.length === 0 ? (
          <div style={{
            padding: '24px 16px', textAlign: 'center',
            color: 'hsl(220 15% 50%)', fontSize: 12,
          }}>
            <Activity size={24} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
            <div>No activity yet</div>
            <div style={{ fontSize: 11, marginTop: 4 }}>
              Start an AI meeting to see agents coding in real-time
            </div>
          </div>
        ) : (
          entries.map((entry) => (
            <div key={entry.id} style={{
              display: 'flex', alignItems: 'flex-start', gap: 6,
              padding: '3px 12px', borderBottom: '1px solid var(--border)',
            }}>
              <span style={{ color: 'hsl(220 15% 45%)', flexShrink: 0, fontSize: 10 }}>
                {entry.time}
              </span>
              <span style={{ fontWeight: 600, color: 'var(--foreground)', flexShrink: 0 }}>
                {entry.agent}
              </span>
              <span style={{ color: actionColor(entry.action) }}>
                {entry.action}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 3, color: 'hsl(200 80% 60%)' }}>
                <FileCode size={10} style={{ flexShrink: 0 }} />
                {entry.file}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
